import React, { useEffect, useState, useMemo, useCallback, lazy, Suspense, useRef } from "react";
import { Table, Dropdown, Icon, Select, Tab, Popup } from "semantic-ui-react";
import { Pie, Column, DualAxes } from "@ant-design/plots";
import { Link, useLocation } from "react-router-dom";
import {
  Permission,
  Fuelsform,
  Form,
  Productionform,
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./FuelsKPIsDashBoard.scss";
import { useLanguage } from "../../../../contexts/LanguageContext";
import FuelSummary from "./FuelSummary";
import TableGraf from "./TableGraf";
import { IntermediateValuesDisplay } from "./IntermediateValuesDisplay";
import KPIExportIconButton from "../KPIExportIconButton";
import { drawFuelsCustomCharts } from "./pdfFuelsCharts";
import { buildFuelsComparisonBase, buildFuelsComparisonData, buildFuelsIntensityBase, buildFuelsIntensityData } from "./fuelsChartData";
import {
  fuelsKPIsCodes,
  fuelsFormulasIndicators,
  determineFuelsUnits,
  fuelsCodes,

  // convertUnitsFinal,
  fuelsTotalMWh,
} from "../../../../utils/dataAndFormulas/fuelsForms";

import { CollapsibleSection, LoadingSpinner } from "../../../Shared";
import {
  convertEnergy2,
  convertUnitsFinal,
} from "../../../../utils/dataAndFormulas/convertEnergy";
import { convertEnergy } from "../../../../utils/dataAndFormulas/convertsUnits";
import { formatWhole } from "../../../../utils/numberFormat";
import { apiRetry } from "../../../../utils";

const permissionController = new Permission();
const fuelsController = new Fuelsform();
const formController = new Form();
const productionController = new Productionform();

export const FuelsKPIsDashBoard = React.memo(function FuelsKPIsDashBoard(props) {
  const { site, sharedProductionForms, sharedWorkersByPeriod, siteName = "" } = props;
  const {
    user: { role },
    accessToken,
  } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  const [permissionActive, setPermissionsActive] = useState([]);
  const [baseline, setbaseline] = useState("");
  const [valueBaseline, setValueBaseline] = useState({});
  // Evita que el cálculo principal corra mientras el fetch de baseline sigue
  // en vuelo (data y baseline se cargan en efectos separados por [site] y
  // pueden resolver en cualquier orden -- mismo fix aplicado en Residuos)
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [missingBaselineYears, setMissingBaselineYears] = useState([]);
  const [indicatorsLineBase, setIndicatorsLineBase] = useState([]);
  // const isAdmin = (role? role.name : "") === "admin";

  const [data, setData] = useState([]);
  const [totales, setTotales] = useState({});
  const [conceptos, setConceptos] = useState([]);
  const [configFormData, setConfigFormData] = useState(null);

  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;

  const [lastYears, setLastYears] = useState({}); // resultados calculados
  const [totalesByFilters, setTotalesByFilters] = useState({}); // resultados calculados
  const [totalesProductionsByFilters, setTotalesProductionsByFilters] =
    useState({}); // resultados de producción por mes
  const [workersTotalsByPeriod, setWorkersTotalsByPeriod] = useState([]); // resultados de trabajadores por periodo

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));
  //declaración estado de filtros
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setPermissionsActive([]);
        if (role) {
          const response = await permissionController.getMyPermissions(accessToken, true);
          setPermissionsActive(response);
        }
      } catch (error) {
        console.error(error);
        //vehiclesKPIsCodes.error(error);
        setPermissionsActive([]);
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setData([]);

        if (site) {
          const prueba = await apiRetry(() => fuelsController.getFuelsFormsByLineBaseYears(accessToken, site));

          setIndicatorsLineBase(prueba.fuelsForms.indicators);
          const yearsLineaBase = prueba.fuelsForms.years;
          const currentYear = new Date().getFullYear();

          // Generar los tres últimos años
          const yearsUltimos5 = [
            currentYear,
            currentYear - 1,
            currentYear - 2,
            currentYear - 3,
            currentYear - 4,
          ].map(String);

          // Combina ambos arrays y elimina duplicados utilizando Set
          const añosCombinados = [
            ...new Set([...yearsLineaBase, ...yearsUltimos5]),
          ];
          const response = await apiRetry(() => fuelsController.getFuelsFormsByLastYears(accessToken, site, añosCombinados));

          setData(response.fuelsForms);
        }
      } catch (error) {
        //console.error(error);
        setData([]);
      } finally {
        stopLoading();
      }
    })();
  }, [site]);

  useEffect(() => {
    (async () => {
      startLoading();
      setbaseline("");
      setBaselineLoaded(false); // al cambiar de sitio, bloquear el cálculo hasta que termine esta carga
      try {
        if (site) {
          const response = await apiRetry(() =>
            formController.getLastBaselineByFormBySite(
              accessToken,
              site,
              "production"
            )
          );

          if (response.code === 200) {
            setbaseline(response.baseline);

            //Para saber el total de los años de baseline sin necesidad de andar consultando varias veces
            const resultado = await Promise.all(
              Object.entries(response.baseline)
                .filter(([anio, valor]) => valor !== null) // Filtra por los valores no nulos
                .map(async ([anio, valor]) => {
                  let a = await getValueBaseLine(anio, valor); // Llamada a la función asincrónica
                  return { [anio]: a }; // Devuelve el año y el valor como un objeto
                })
            );

            // Combina todos los objetos en un único objeto final
            const resultadoFinal = resultado.reduce((acc, obj) => {
              return { ...acc, ...obj }; // Combina los objetos
            }, {});

            setValueBaseline(resultadoFinal);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        stopLoading();
        setBaselineLoaded(true);
      }
    })();
  }, [site]);

  // Obtener configuración de campos activos del formulario
  useEffect(() => {
    (async () => {
      try {
        if (site) {
          const response = await formController.getFormsBySite(
            accessToken,
            site,
            "fuels",
            true
          );
          // La respuesta es directamente un array de formularios
          if (Array.isArray(response) && response.length > 0) {
            setConfigFormData(response[0]);
          } else {
            setConfigFormData(null);
          }
        }
      } catch (error) {
        console.error("Error al obtener configFormData:", error);
        setConfigFormData(null);
      }
    })();
  }, [site]);

  // Función que obtiene los campos fuente de un KPI basándose en fuelsFormulasIndicators
  const getKPISourceFields = (kpiName) => {
    // Mapeo basado en la lógica real de fuelsFormulasIndicators
    const kpiSourceMap = {
      // KPIs con campos fuente directos
      '_natural_gas': ['natural_gas', 'natural_gas_vehicles'],
      '_glp': ['glp', 'glp_vehicles'],
      '_diesel': ['diesel', 'diesel_vehicles'],
      '_biodiesel': ['biodiesel', 'biodiesel_vehicles'],
      '_gasoline': ['gasoline', 'gasoline_vehicles'],
      '_ethanol': ['ethanol', 'ethanol_vehicles'],
      '_electricity_vehicles': ['electricity_vehicles', 'electric_vehicles'],

      // KPIs calculados/agregados (no filtrar por campos individuales)
      'total_fuel_mwh': null, // Agregación de todos los combustibles
      'total_vehicles': null, // Suma de todos los vehículos
      'fuel_vehicle': null, // Calculado: total_fuel_mwh / total_vehicles
      'total_fuel_cost': null, // Agregación de costos
    };

    return kpiSourceMap[kpiName];
  };

  // Determinar los campos únicos y si tienen código
  // Filtra solo los campos que están activos en configFormData
  const determineFieldsKPIs = () => {
    const fields = new Set();

    Object.keys(fuelsKPIsCodes).forEach((key) => {
      const sourceFields = getKPISourceFields(key);

      // Si no tiene campos fuente (es un KPI calculado/agregado), siempre agregarlo
      if (sourceFields === null) {
        fields.add(key);
        return;
      }

      // Si hay configFormData, verificar si TODOS los campos fuente están activos
      if (configFormData?.fields && configFormData.fields.length > 0) {
        const allSourceFieldsActive = sourceFields.every(sourceFieldName => {
          const fieldConfig = configFormData.fields.find(f => f.name === sourceFieldName);
          return fieldConfig && fieldConfig.active !== false;
        });

        if (allSourceFieldsActive) {
          fields.add(key);
        }
      } else {
        // Si no hay configFormData, mostrar todos los campos (comportamiento por defecto)
        fields.add(key);
      }
    });

    return Array.from(fields);
  };

  // Determinar los campos únicos y si tienen código
  const determineFieldsCode = () => {
    const fields = new Set();

    Object.keys(fuelsCodes).forEach((key) => {
      fields.add(key);
    });

    return Array.from(fields);
  };

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        if (data && baselineLoaded) {
          const previous_totals = await transformData1(data);
          calculateDataFinal(previous_totals);
          /* }
      } catch (error) {
        //console.error(error);
      } finally {
        stopLoading();
      }
    })();
*/

          // Detectar años sin baseline para mostrar aviso
          const currentYear = new Date().getFullYear();
          const yearsToCheck = [0,1,2,3,4].map(i => String(currentYear - i));
          setMissingBaselineYears(yearsToCheck.filter(y => !valueBaseline?.[y]));

          // 🔹 Últimos 5 años dinámicos
          const lastFiveYears = [
            currentYear,
            currentYear - 1,
            currentYear - 2,
            currentYear - 3,
            currentYear - 4,
          ];

          // 🔹 Filtrar datos solo de esos 5 años
          const filteredByYears = data?.data
            ?.filter((item) => lastFiveYears.includes(Number(item.year)))
            ?.reduce((acc, item) => {
              const year = Number(item.year);
              if (!acc[year]) acc[year] = [];
              acc[year].push(item);
              return acc;
            }, {});

          // 🔹 Guardar los últimos cinco años en estado
          setLastYears(lastFiveYears);

          const results = await getLastThreeYearsData(filteredByYears);
          setTotalesByFilters(results);

          // Usar datos compartidos desde DashBoard.js
          if (sharedProductionForms) {
            const totalesFinal = await calcularVPSVFSporMesPorAño(sharedProductionForms, baseline);
            setTotalesProductionsByFilters(totalesFinal);
          }

          if (sharedWorkersByPeriod) {
            setWorkersTotalsByPeriod(sharedWorkersByPeriod);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        stopLoading();
      }
    })();
  }, [data, valueBaseline, baselineLoaded, configFormData, sharedProductionForms, sharedWorkersByPeriod]);

  //Calcula el valor de linea base por año hay que cambiar si es por vfs o vps
  const getValueBaseLine = useCallback(async (year, type) => {
    try {
      if (type === "VPS") {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_production"
          );
        //setValueBaseline(response1.total)

        return response1.total;
      } else {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_billing"
          );

        return response1.total;
      }
    } catch (error) {
      console.error(error);
      return 1;
    }
  }, [accessToken, site]);

  async function calcularVPSVFSporMesPorAño(documentosPorAño, baselines) {
    const resultado = {};

    for (const [year, docs] of Object.entries(documentosPorAño)) {
      const baselineType = baselines[year]; // "VPS" o "VFS"
      if (!baselineType) continue;

      const isProduccion = baselineType === "VPS";
      const prefix = isProduccion ? "VP" : "VF";

      resultado[year] = {};

      for (const doc of docs) {
        const mes = doc.period;
        if (!mes) continue;

        let totalMes = 0;

        // Recorremos todos los campos del documento
        for (const [key, campo] of Object.entries(doc)) {
          // buscamos solo los campos dinámicos de producción o facturación
          if (
            campo?.code &&
            campo.code.startsWith(prefix) &&
            !campo.code.startsWith(prefix + "S")
          ) {
            const value = Number(campo.value) || 0;
            const coef = Number(campo.standardized_coefficient) || 1;
            totalMes += value * coef;
          }
        }

        resultado[year][mes] = totalMes;
      }
    }

    return resultado;
  }

  const transformData1 = async (data) => {
    // Inicializar el objeto de totales y los conceptos
    const totalsPerYear = {};
    const conceptsSet = [];
    const unitsCache = {}; // Aquí almacenaremos los datos de unidades por año

    const fields = determineFieldsCode();
    // Verificar si data y data.data son válidos y contienen datos
    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      //console.log("No hay datos para procesar o data.data no es un array válido");
      return {}; // Retornar un objeto vacío o manejar el caso según se requiera
    }
    // Crear un array de años únicos de los datos de consumo
    const uniqueYears = [
      ...new Set(data.data.map((item) => String(item.year))),
    ];

    // Primero, obtener las unidades para todos los años requeridos
    //console.log('Obteniendo unidades para los años:', uniqueYears);
    const promises = uniqueYears.map(async (año) => {
      try {
        // Realizar la llamada para obtener las unidades para el año
        const units = await fuelsController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields
        );

        // Guardar en el caché si las unidades son válidas
        if (units && units.fields) {
          unitsCache[año] = units;
          //console.log(`Unidades guardadas en caché para el año ${año}:`, units);
        } else {
          // console.log(`No se obtuvieron unidades válidas para el año ${año}`);
        }
      } catch (error) {
        //console.log(`Error al obtener unidades para el año ${año}:`, error);
      }
    });

    // Esperar a que todas las consultas de unidades se resuelvan
    await Promise.all(promises);

    // Ahora procesamos los datos de consumo usando las unidades en caché
    // console.log('Procesando los consumos');
    if (data.data !== undefined) {
      // Crear un array de promesas para manejar las llamadas asíncronas
      const consumptionPromises = data.data.map(async (item) => {
        const year = String(item.year);

        // Verificar si ya tenemos las unidades para este year en el caché
        const units = unitsCache[year];
        if (!units) {
          //console.log(`No se encontraron unidades para el year ${year} en el caché`);
          return;
        }

        // Iterar sobre los conceptos que tenemos en el item
        fields.forEach((concept) => {
          // Si el concepto existe, procesamos
          if (item[concept]) {
            // Añadir el concepto a la lista de concepts únicos
            conceptsSet.push(concept);

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept]) {
              totalsPerYear[concept] = {};
            }

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept][year]) {
              totalsPerYear[concept][year] = {
                total: 0,
                unit: units.fields[concept] || item[concept]?.unit || "",
                validMonths: 0, // ✅ Nuevo: Contador de meses con datos > 0
              };
            }

            // // ❌ Código viejo: Sumar el valor de consumo
            // totalsPerYear[concept][year].total += item[concept].value
            //   ? convertUnitsFinal(
            //       item[concept].value,
            //       item[concept].unit,
            //       totalsPerYear[concept][year].unit,
            //       concept
            //     )
            //   : 0;

            // ✅ Código nuevo: Calcular el valor convertido y contar meses válidos
            const convertedValue = item[concept].value
              ? convertUnitsFinal(
                  item[concept].value,
                  item[concept].unit,
                  totalsPerYear[concept][year].unit,
                  concept
                )
              : 0;

            // Sumar el valor de consumo
            totalsPerYear[concept][year].total += convertedValue;

            // Incrementar contador de meses válidos si el valor > 0
            if (convertedValue > 0) {
              totalsPerYear[concept][year].validMonths += 1;
            }
          }
        });
      });
      // Esperar a que todas las promesas de consumo se resuelvan
      await Promise.all(consumptionPromises);
    }

    return totalsPerYear;
  };

  const calculateDataFinal = async (totales) => {
    // Inicializar el objeto de totales y los conceptos

    const totalesFinal = { ...totales };
    const conceptosSet = [];

    if (totales !== undefined) {
      for (let item in totales) {
        if (totales.hasOwnProperty(item)) {
          for (let year in totales[item]) {
            //const valueB = await getValueBaseLine(year, baseline[year]);
            const valueB = valueBaseline[year];
            if (totales[item].hasOwnProperty(year)) {
              const fields = determineFieldsKPIs();

              // Iterar sobre los conceptos que tenemos en el item
              fields.forEach((concepto) => {
                // Añadir el concepto a la lista de conceptos únicos
                // Inicializar el total para el año si no existe
                if (!totalesFinal[concepto]) {
                  totalesFinal[concepto] = {};
                  conceptosSet.push(concepto);
                }

                // Inicializar el total para el año si no existe
                if (!totalesFinal[concepto][year]) {
                  totalesFinal[concepto][year] = {};
                }

                totalesFinal[concepto][year] = fuelsFormulasIndicators(
                  concepto,
                  year,
                  totalesFinal,
                  valueB === 0 ? 1 : valueB
                );
              });
            }
          }
        }
      }
    }

    //setConceptos(removeNulls(totalesFinal, conceptosSet));

    setConceptos(conceptosSet);
    setTotales(totalesFinal);
  };
  // Función para obtener los valores de un año y mes específicos (period en texto)
  const getDataByYearAndMonth = async (data, year, month) => {
    const results = {};
    const fields = determineFieldsCode();
    const unitsCache = {};

    if (!data || !data || Object.keys(data).length === 0) {
      return {};
    }

    // Diccionario de meses
    const monthMap = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    const yearStr = String(year);
    const monthStr =
      typeof month === "string"
        ? monthMap[month] || ""
        : String(month).padStart(2, "0");

    if (!monthStr) {
      console.warn("Mes inválido o no reconocido:", month);
      return {};
    }

    const yearData = data[yearStr];
    if (!Array.isArray(yearData)) {
      console.warn("No hay datos para el año:", yearStr);
      return {};
    }

    const filteredData = yearData.filter(
      (item) => monthMap[item.period] === monthStr
    );

    if (filteredData.length === 0) {
      console.warn(`No hay datos para ${month} ${yearStr}`);
      return {};
    }

    // Procesar resultados por campo
    filteredData.forEach((item) => {
      const units = unitsCache[yearStr];

      fields.forEach((concept) => {
        if (item[concept]) {
          if (!results[concept]) {
            results[concept] = {
              total: 0,
              unit: units?.fields[concept] || item[concept]?.unit || "",
            };
          }

          results[concept].total += item[concept].value
            ? convertUnitsFinal(
                item[concept].value,
                item[concept].unit,
                results[concept].unit,
                concept
              )
            : 0;
        }
      });
    });

    // ==========================
    // 🔹 Calcular energía total (sin incluir costos)
    // ==========================

    // Lista de conceptos energéticos (sin costos)
    const fuelsConceptos = Object.keys(fuelsCodes).filter(
      (key) => !key.includes("cost")
    );

    let totalFuelsMonth = 0;
    let fuelsUnit = "";

    fuelsConceptos.forEach((concept) => {
      if (results[concept] && typeof results[concept].total === "number") {
        totalFuelsMonth += results[concept].total;
        fuelsUnit = results[concept].unit || fuelsUnit;
      }
    });

    results.total_fuels_month = {
      total: totalFuelsMonth,
      unit: fuelsUnit || "MWh",
    };

    // ==========================
    // 🔹 Calcular renovables
    // ==========================

    // ==========================
    // 🔹 Calcular electrica
    // ==========================
    return results;
  };

  // ==========================
  // 🔹 Calcular ultimos 3 años
  // ==========================

  const getLastThreeYearsData = async (totalesByFilters) => {
    const currentYear = new Date().getFullYear();
    const lastThreeYears = [currentYear - 2, currentYear - 1, currentYear];

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const resultsByYear = {};

    for (const year of lastThreeYears) {
      resultsByYear[year] = {};

      for (const month of months) {
        const data = await getDataByYearAndMonth(totalesByFilters, year, month);
        resultsByYear[year][month] = data;
      }
    }

    return resultsByYear;
  };

  const removeNulls = (data, conceptos) => {
    // Iterar sobre las claves externas
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        // Verificar si todos los valores internos son 0
        const allZero = Object.values(data[key]).every(
          (value) => value.total === 0
        );

        // Si todos los valores son 0, eliminar la clave externa
        if (allZero) {
          //delete conceptos[key];
          conceptos = conceptos.filter((item) => item !== key);
        }
      }
    }
    return conceptos;
  };

  const summaryRef = useRef();
  const tableGrafRef = useRef();
  const comparisonChartRef = useRef();
  const intensityChartRef = useRef();
  // Combustible seleccionado en cada gráfico combinado — vive acá (no como
  // useState local de cada componente) para que el export a PDF sepa qué
  // combustible estaba mostrando cada uno al momento de exportar.
  const [comparisonFuel, setComparisonFuel] = useState(null);
  const [intensityFuel, setIntensityFuel] = useState(null);

  const yearsWithData = useMemo(() => {
    if (!data?.years || !conceptos || conceptos.length === 0) return [];
    return [...data.years]
      .sort((a, b) => b - a)
      .slice(0, 5)
      .filter(year =>
        conceptos.some(item => (totales[item]?.[year]?.total || 0) !== 0)
      );
  }, [data, conceptos, totales]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (
    !data ||
    !data.years ||
    data.years.length === 0 ||
    !conceptos ||
    conceptos.length === 0
  ) {
    return (
      <div style={{ textAlign: "center", padding: "5em 0", color: "#777" }}>
        <div style={{ fontSize: "2em", marginBottom: "1em", color: "#ccc" }}>
          <Icon name="exclamation triangle" size="huge" color="gray" />
        </div>
        <div
          style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: "1em" }}
        >
          {t("no_data_available") || "No hay datos disponibles"}
        </div>
      </div>
    );
  } else {
    const exportYears = data?.years ? [...data.years].sort((a, b) => Number(b) - Number(a)).slice(0, 5) : [];

    // AJUSTES DE MAGNITUDES NUMÉRICAS aplicado a la tabla del PDF (antes
    // usaba `toFixed(2)` fijo + coma decimal). Regla 1 para todas las
    // filas, igual que formatWithDecimals de la tabla en pantalla (todos
    // los indicadores de Combustibles son MWhe/kWhe/costo, sin casos que
    // necesiten decimales).
    const formatFuelsCellForPdf = (value) => {
      if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
      // autoTable dibuja esta celda con doc.text() nativo (no SVG) — el
      // espacio fino (U+202F) no existe en WinAnsi y rompe el texto (mismo
      // problema ya resuelto en GEI/Social/Residuos/Efluentes/Agua).
      return formatWhole(value).replace(/ /g, " ");
    };

    return (
      <div className="list-fuels-forms">

        {/* Aviso cuando hay años sin baseline VPS/VFS */}
        {missingBaselineYears.length > 0 && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "6px",
            padding: "10px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontSize: "0.92em",
          }}>
            <Icon name="warning sign" color="yellow" />
            <span>
              Algunos indicadores (KPIs) para los años <strong>{missingBaselineYears.join(", ")}</strong> mostrarán valor 0. Configurá el volumen producido (VPS) o el volumen facturado (VFS) como línea base para verlos correctamente.{" "}
              <Link to="/admin/data/productionforms" style={{ color: "#856404", fontWeight: "bold" }}>
                Configurar línea base →
              </Link>
            </span>
          </div>
        )}

        {/* Sección de Resumen */}
        <div className="fuels-summary-section" ref={summaryRef}>
          <FuelSummary
            filters={filters}
            site={site}
            valueBaseline={valueBaseline}
            fuelsController={Fuelsform}
            totales={totales}
            selectorSlot={<YearMonthSelector onChange={setFilters} availableYears={yearsWithData} />}
            exportSlot={
              <KPIExportIconButton
                title={t("fuels") || "Combustibles"}
                conceptos={conceptos}
                years={exportYears}
                totales={totales}
                codes={fuelsKPIsCodes}
                t={t}
                codes={fuelsKPIsCodes}
                formatCellValue={formatFuelsCellForPdf}
                chartRefs={[
                  { ref: summaryRef, label: t("summary") || "Resumen" },
                ]}
                renderCustomCharts={(doc, chartCtx) => drawFuelsCustomCharts(doc, chartCtx, {
                  totales,
                  filters,
                  resultsByYear: totalesByFilters,
                  comparisonFuel,
                  intensityFuel,
                  t,
                })}
                siteName={siteName}
              />
            }
          />
        </div>

        {/* Sección de Gráficos */}
        <div className="fuels-tablegraf-section" ref={tableGrafRef}>
          <TableGraf filters={filters} totales={totales} t={t}/>
        </div>

        {/* Sección de Comparación */}
        <CollapsibleSection
          title={t("fuel_comparison_last_3_years")}
          className="fuels-comparison-section"
          ref={comparisonChartRef}
        >
          <FuelsComparisonChart
            resultsByYear={totalesByFilters}
            t={t}
            selectedFuel={comparisonFuel}
            onSelectedFuelChange={setComparisonFuel}
          />
        </CollapsibleSection>

        {/* Sección de Intensidad */}
        <CollapsibleSection
          title={t("intensity_by_fuel_type")}
          className="fuels-intensity-section"
          ref={intensityChartRef}
        >
          <FuelsIntensityByTypeChart
            resultsByYear={totalesByFilters}
            t={t}
            filters={filters}
            selectedFuel={intensityFuel}
            onSelectedFuelChange={setIntensityFuel}
          />
        </CollapsibleSection>

        {/* Sección de Tabla de Datos */}
        <TableYears
          filters={filters}
          data={data}
          accessToken={accessToken}
          t={t}
          conceptos={conceptos}
          totales={totales}
          language={language}
          indicatorsLineBase={indicatorsLineBase}
          valueBaseline={valueBaseline}
          defaultOpen={true}
        />

        {/* Sección de Valores Intermedios */}
        {/* <IntermediateValuesDisplay
          resultsProductions={totalesProductionsByFilters}
          resultsWorkers={workersTotalsByPeriod}
          t={t}
        /> */}
      </div>
    );
  }
});

function TableYears(props) {
  const { data, conceptos, totales, t, language, indicatorsLineBase, filters, defaultOpen = true } =
    props;
// console.log("📊 TableYears props:", data);
  const { year, month } = filters || {};
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [years, setYears] = useState([]);
  //const [listConceptos, setListConceptos] = useState([]);
  const [months, setMoths] = useState([]);
  const [listConceptos, setListConceptos] = useState([]);

  //const [uniqueFields, setUniqueFields] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // Función para manejar cambios de unidad (comentada para uso futuro)
  /*const handleUnitChange = (rowIndex, newValue) => {
    setSelectedUnits({ ...selectedUnits, [rowIndex]: newValue.value });
  };*/
  useEffect(() => {
    // console.log("📊 TableGraf filtrando con:", { year, month });
  }, [year, month]);

  useEffect(() => {
    (async () => {
      try {
        setYears(data.years ? data.years.sort((a, b) => b - a).slice(0, 5) : []);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [data]);

  // Buscar el primer año que tenga unidad definida
  const obtenerUnidadDeOtrosAnios = (datos, concepto) => {
    if (!datos[concepto]) return "";
    const años = Object.keys(datos[concepto]);
    const añoConUnidad = años.find((año) => datos[concepto][año]?.unit);
    return añoConUnidad ? datos[concepto][añoConUnidad].unit : "";
  };

  useEffect(() => {
    (async () => {
      try {
        // Obtener las unidades buscando el primer año con unidad definida
        let valor = {};
        conceptos.map((field) => {
          valor[field] = obtenerUnidadDeOtrosAnios(totales, field);
        });
        setSelectedUnits(valor);
      } catch (error) {
        //console.error(error);
        setSelectedUnits([]);
      }
    })();
  }, [conceptos, totales]);

  useEffect(
    () => {
      (async () => {
        try {
          setListConceptos(conceptos);
        } catch (error) {
          console.error(error);
        }
      })();
    }, // [conceptos, setListConceptos]); //[conceptos]
    [conceptos]
  );

  const getIndicators = (item) => {
    let result = indicatorsLineBase.find((value) => value.name === item);
    if (result) {
      return result?.yearBaseline || 0;
    }
    return 0; // Sin cambio
  };

  // Regla 1 (espacio fino separador de miles, sin decimales) para todas las filas de esta tabla
  const formatWithDecimals = (value) => formatWhole(value);

  // Renderiza una píldora de comparativa rojo/verde
  const renderPill = (value, prevValue) => {
    const numValue = value != null ? Number(value) : null;
    const numPrev = prevValue != null ? Number(prevValue) : null;

    if (numValue == null || numValue === 0) {
      return <span className="gei-value-pill pill-empty" />;
    }

    let pillClass;
    if (numPrev == null || numPrev === 0) {
      pillClass = "pill-neutral";
    } else if (numValue > numPrev) {
      pillClass = "pill-up";
    } else if (numValue < numPrev) {
      pillClass = "pill-down";
    } else {
      pillClass = "pill-neutral";
    }

    return (
      <span className={`gei-value-pill ${pillClass}`}>
        {formatWithDecimals(numValue)}
      </span>
    );
  };

  // Filtrar conceptos que tienen todos los años en cero
  const filteredConceptos = useMemo(() => {
    return conceptos.filter((item) => {
      // Si no hay datos para este concepto, no mostrarlo
      if (!totales[item]) return false;

      // Verificar si hay al menos un año con valor > 0
      const hasNonZeroValue = years.some((year) => {
        const value = totales[item][year]?.total || 0;
        return value > 0;
      });

      return hasNonZeroValue;
    });
  }, [conceptos, totales, years]);

// console.log("📊 TableYears total:", totales, conceptos);
  return (
    <>
      <div className="kpi-table-section">
      <div className="section-content">
      <Table celled structured size="small" className="gei-kpi-table" unstackable>
        <Table.Header>
          <Table.Row
            onClick={() => setIsOpen(!isOpen)}
            style={{ cursor: "pointer" }}
          >
            <Table.HeaderCell textAlign="center">{t("code")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">{t("indicators")}</Table.HeaderCell>
            {years.map((year, index) => (
              <Table.HeaderCell textAlign="center" key={index}>{year}</Table.HeaderCell>
            ))}
            <Table.HeaderCell textAlign="center">{t("LB")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="center" style={{ width: "200px" }}>
              <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {t("units")}
                <Icon name={isOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        {isOpen && <Table.Body>
          {filteredConceptos.map((item, index) => {
            // Recopilar todos los valores de la fila para determinar decimales consistentes
            const rowValues = years
              .map(y => convertUnitsFinal(
                totales[item]?.[y]?.total || 0,
                totales[item]?.[y]?.unit,
                selectedUnits[item],
                item
              ))
              .filter(v => typeof v === "number" && !isNaN(v));
            const lbConverted = convertUnitsFinal(
              totales[item]?.[getIndicators(item)]?.total || 0,
              totales[item]?.[getIndicators(item)]?.unit,
              selectedUnits[item],
              item
            );
            if (typeof lbConverted === "number" && !isNaN(lbConverted)) rowValues.push(lbConverted);

            return (
            <React.Fragment key={index}>
              <Table.Row>
                <Table.Cell data-label={t("code")}>{fuelsKPIsCodes[item]}</Table.Cell>
                <Table.Cell data-label={t("indicators")}>{t(item)}</Table.Cell>
                {years.map((year, i) => {
                  if (totales[item]) {
                    const rawValue = convertUnitsFinal(
                      totales[item][year]?.total || 0,
                      totales[item][year]?.unit,
                      selectedUnits[item],
                      item
                    );
                    const prevRawValue = convertUnitsFinal(
                      totales[item][years[i + 1]]?.total || 0,
                      totales[item][years[i + 1]]?.unit,
                      selectedUnits[item],
                      item
                    );
                    const pill = renderPill(rawValue, prevRawValue);
                    return (
                      <Table.Cell key={i} textAlign="center" data-label={year}>
                        <Popup
                          content={`Valor real: ${parseFloat(Number(totales[item][year]?.total || 0).toFixed(6))} ${totales[item][year]?.unit || selectedUnits[item]}`}
                          trigger={<span style={{ cursor: "help" }}>{pill}</span>}
                          position="top center"
                          size="tiny"
                        />
                      </Table.Cell>
                    );
                  }
                  return <Table.Cell key={i} data-label={year}>{""}</Table.Cell>;
                })}
                <Table.Cell data-label={t("LB")}>
                  {(() => {
                    const lbValue = convertUnitsFinal(
                      totales[item][getIndicators(item)]?.total || 0,
                      totales[item][getIndicators(item)]?.unit,
                      selectedUnits[item],
                      item
                    );
                    return (
                      <Popup
                        content={`Valor real: ${parseFloat(Number(totales[item][getIndicators(item)]?.total || 0).toFixed(6))} ${totales[item][getIndicators(item)]?.unit || selectedUnits[item]}`}
                        trigger={
                          <span style={{ cursor: "help" }}>
                            {formatWithDecimals(lbValue)}
                          </span>
                        }
                        position="top center"
                        size="tiny"
                      />
                    );
                  })()}
                </Table.Cell>
                <Table.Cell data-label={t("units")}>
                  {selectedUnits[item]
                    ? (item.startsWith('_') || item === 'fuel_vehicle')
                      ? `${selectedUnits[item]}e / ${t("vehicles")}`
                      : (item.includes('per_unit') ||
                         item === 'total_fuels_consumption_kpis' ||
                         item === 'renewable_fuels_consumption_kpis' ||
                         item === 'non_renewable_fuels_consumption_kpis')
                        ? `${selectedUnits[item]} / ${t("unit")}`
                        : item === 'total_fuel_mwh'
                          ? `${selectedUnits[item]}e`
                          : selectedUnits[item]
                    : "-"}
                  {/* Dropdown para convertir unidades (comentado para uso futuro)
                  <Dropdown
                    onChange={(e, data) => {
                      handleUnitChange(item, data);
                    }}
                    options={determineFuelsUnits(item).map((ds) => {
                      return {
                        key: ds.key,
                        text: ds.name,
                        value: ds.value,
                      };
                    })}
                    search
                    inline
                    defaultValue={determineFuelsUnits(item)[0].value}
                  />
                  */}
                </Table.Cell>
                {/* <Table.Cell>
                  {(() => {
                    const mwhValue = convertEnergy2(
                      totales[item][year]?.total || 0,
                      totales[item][year]?.unit,
                      "mwh",
                      item,
                      true
                    );
                    return (
                      <Popup
                        content={`Valor exacto: ${mwhValue || 0}`}
                        trigger={
                          <span style={{ cursor: "help" }}>
                            {formatWithDecimals(mwhValue, rowDecimals)}
                          </span>
                        }
                        position="top center"
                        size="tiny"
                      />
                    );
                  })()}
                </Table.Cell> */}
              </Table.Row>
            </React.Fragment>
            );
          })}
        </Table.Body>}
      </Table>
      </div>
      </div>
    </>
  );
}

export const YearMonthSelector = ({ onChange, availableYears = [] }) => {
  const currentYear = new Date().getFullYear();

  const years = availableYears.length > 0
    ? [...availableYears].sort((a, b) => b - a).map(y => ({ key: y, text: y, value: y }))
    : [
        { key: currentYear, text: currentYear, value: currentYear },
        { key: currentYear - 1, text: currentYear - 1, value: currentYear - 1 },
        { key: currentYear - 2, text: currentYear - 2, value: currentYear - 2 },
        { key: currentYear - 3, text: currentYear - 3, value: currentYear - 3 },
        { key: currentYear - 4, text: currentYear - 4, value: currentYear - 4 },
      ];

  const months = [
    { key: 1, text: "Enero", value: 1 },
    { key: 2, text: "Febrero", value: 2 },
    { key: 3, text: "Marzo", value: 3 },
    { key: 4, text: "Abril", value: 4 },
    { key: 5, text: "Mayo", value: 5 },
    { key: 6, text: "Junio", value: 6 },
    { key: 7, text: "Julio", value: 7 },
    { key: 8, text: "Agosto", value: 8 },
    { key: 9, text: "Septiembre", value: 9 },
    { key: 10, text: "Octubre", value: 10 },
    { key: 11, text: "Noviembre", value: 11 },
    { key: 12, text: "Diciembre", value: 12 },
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12

  useEffect(() => {
    if (availableYears && availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      const sorted = [...availableYears].sort((a, b) => b - a);
      const newYear = sorted[0];
      setSelectedYear(newYear);
      onChange({ year: newYear, month: selectedMonth });
    }
  }, [availableYears]);

  const handleYearChange = (e, { value }) => {
    setSelectedYear(value);
    onChange({ year: value, month: selectedMonth });
  };

  const handleMonthChange = (e, { value }) => {
    setSelectedMonth(value);
    onChange({ year: selectedYear, month: value });
  };

  return (
    <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
      <Dropdown
        placeholder="Año"
        selection
        options={years}
        value={selectedYear}
        onChange={handleYearChange}
        className="year-filter-dropdown"
      />
      {/* <Dropdown
        placeholder="Mes"
        selection
        options={months}
        value={selectedMonth}
        onChange={handleMonthChange}
      /> */}
    </div>
  );
};



function FuelsComparisonChart({ resultsByYear, t, selectedFuel, onSelectedFuelChange }) {
  // Datos base (años + combustibles con datos) y series del gráfico —
  // misma lógica que antes, extraída a fuelsChartData.js para poder
  // reusarla también desde el export a PDF (pdfFuelsCharts.js).
  const { years, fuels } = useMemo(() => buildFuelsComparisonBase(resultsByYear), [resultsByYear]);

  // selectedFuel ahora es una prop controlada (vive en FuelsKPIsDashBoard,
  // no acá) para que el export a PDF sepa qué combustible mostrar. Si
  // todavía no llegó un valor válido del padre, se usa el mismo default de
  // siempre (el primero con datos) como fallback LOCAL e inmediato — así el
  // primer render ya muestra el gráfico completo, en vez de un frame vacío
  // seguido de otro con datos (ese salto, con la animación de entrada de
  // G2, se veía como barras superpuestas/fantasma). El efecto solo avisa el
  // default al padre en paralelo, sin que el gráfico dependa de esa vuelta.
  const effectiveSelectedFuel =
    selectedFuel && fuels.some((f) => f.key === selectedFuel)
      ? selectedFuel
      : fuels[0]?.key || "total";

  useEffect(() => {
    if (fuels.length === 0) return;
    if (!selectedFuel || !fuels.some((f) => f.key === selectedFuel)) {
      onSelectedFuelChange(fuels[0]?.key || "total");
    }
  }, [fuels, selectedFuel, onSelectedFuelChange]);

  const { chartData, averageData } = useMemo(
    () => buildFuelsComparisonData(resultsByYear, t, effectiveSelectedFuel),
    [resultsByYear, t, effectiveSelectedFuel]
  );

  const selectedFuelConfig = fuels.find((f) => f.key === effectiveSelectedFuel);
// Renderizador de la leyenda estilo "Pills"
const CustomLegend = () => (
  <div style={{
    display: "flex",
    justifyContent: "flex-start", // 👈 Alinea a la izquierda
    gap: "10px",
    marginBottom: "20px",
    paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
    flexWrap: "wrap",
  }}>
    {years.map((year) => {
      const isActive = activeYears.includes(year);
      return (
        <div
          key={year}
          onClick={() => toggleYear(year)}
          style={{
            backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
            color: isActive ? "white" : "#9ca3af",
            padding: "4px 20px", // Un poco más compacto
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
            boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth:"100px"
          }}
        >
          {year}
        </div>
      );
    })}
  </div>
);

// Dentro de tu componente FuelsComparisonChart
const [activeYears, setActiveYears] = useState(years); 

// Función para alternar años
const toggleYear = (year) => {
  if (activeYears.includes(year)) {
    // Si solo queda uno, podrías elegir no dejar que se desactive el último
    setActiveYears(activeYears.filter(y => y !== year));
  } else {
    setActiveYears([...activeYears, year]);
  }
};

const filteredChartData = useMemo(() => 
  chartData.filter(d => activeYears.includes(String(d.year))), 
[chartData, activeYears]);

const filteredAverageData = useMemo(() => 
  averageData.filter(d => activeYears.includes(String(d.year))), 
[averageData, activeYears]);
  // 🔹 Leer colores desde CSS
const getCssVar = (name) =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();

const chartColors = {
  oldest: getCssVar("--chart-year-oldest"),
  prev: getCssVar("--chart-year-prev"),
  last: getCssVar("--chart-year-last"),
  avg: getCssVar("--chart-avg-line"),
};

// // 🔹 Mapear años → colores fijos
// const yearColorMap = useMemo(() => {
//   const sorted = [...years].map(Number).sort((a, b) => a - b);

//   return {
//     [sorted[0]]: chartColors.oldest,
//     [sorted[1]]: chartColors.prev,
//     [sorted[2]]: chartColors.last,
//   };
// }, [years]);

// 1. Mapear años → colores fijos (Asegúrate de que esto esté actualizado)
const yearColorMap = useMemo(() => {
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: chartColors.oldest || "#cbd5e1", // Fallback si el CSS no carga
    [sorted[1]]: chartColors.prev || "#94a3b8",
    [sorted[2]]: chartColors.last || "#1e293b",
  };
}, [years, chartColors]);


  // Escala Y explícita y compartida entre barras y línea de promedio: "sync" por sí
  // solo no alineaba correctamente (mismo caso que en Energía/Intensidad de Vehículos).
  const combinedMax = Math.max(
    0,
    ...filteredChartData.map((d) => d.value || 0),
    ...filteredAverageData.map((d) => d.value || 0),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  const sharedYScale = { domainMin: 0, domainMax: yDomainMax, nice: true };

  const config = {
  xField: "month",
  legend: false,

  scale: {
    color: {
      domain: Object.keys(yearColorMap),
      range: Object.values(yearColorMap),
    },
  },

  tooltip: {
    items: [
      (item) => ({
        name: item.type,
        value: `${formatWhole(item.value)} kWhe`,
      }),
    ],
  },

  children: [
    {
      data: filteredChartData,
      type: "interval",
      yField: "value",
      xField: "month",
      colorField: "year",
      group: true,
      dodgePadding: 2,
      intervalPadding: 4,
      style: ({ value }) => ({
        fillOpacity: value === 0 ? 0 : 1,
      }),
      scale: { y: sharedYScale },
      interaction: { elementHighlight: { background: true } },
    },
    {
      data: filteredAverageData,
      type: "line",
      yField: "value",
      xField: "month",
      colorField: "year",
      // OCULTAR EJE Y DEL HIJO: Evita que aparezcan números a la derecha
      axis: { y: false },
      scale: { y: sharedYScale },
      style: {
        lineWidth: 2,
        lineDash: [4, 8],
      },
      interaction: {
        tooltip: {
          crosshairs: false,
          marker: false,
        },
      },
    },
  ],

  // 3️⃣ EJE ÚNICO (Izquierdo)
  axis: {
    x: {
      title: false,
      labelFill: "#000",
    },
    y: {
      title: false,
      grid: true,
      labelFill: "#000",
    }
  },
};

  

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {/* 🔘 Selector de combustible */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "0.6rem",
          marginBottom: "1.5rem",
          marginTop: "1rem",
        }}
      >
        {fuels.map((fuel) => (
          <button
            key={fuel.key}
            onClick={() => onSelectedFuelChange(fuel.key)}
            style={{
              backgroundColor:
                effectiveSelectedFuel === fuel.key ? "#648EBC" : "#f3f4f6",
              color: effectiveSelectedFuel === fuel.key ? "white" : "#374151",
              border: "none",
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            {fuel.label}
          </button>
        ))}
      </div>

      {/* <h4
        style={{
          textAlign: "center",
          marginBottom: "0.5rem",
          color: "#555",
        }}
      >
        {selectedFuelConfig.label}
      </h4> */}
{/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
    {chartData.length > 0 && <CustomLegend />}

      {chartData.length ? (
        <div style={{ height: 400 }}>
         <DualAxes {...config} style={{ height: 400 }} />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 400,
            color: "#999",
          }}
        >
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

function FuelsIntensityByTypeChart({ resultsByYear = {}, t, selectedFuel, onSelectedFuelChange }) {
  // Datos base (años + tipos de combustible con datos) y series del
  // gráfico — misma lógica que antes, extraída a fuelsChartData.js para
  // poder reusarla también desde el export a PDF (pdfFuelsCharts.js).
  const { years, fuelTypes } = useMemo(() => buildFuelsIntensityBase(resultsByYear), [resultsByYear]);

  // selectedFuel ahora es una prop controlada (vive en FuelsKPIsDashBoard,
  // no acá) — mismo fallback local inmediato que en FuelsComparisonChart
  // (ver comentario ahí) para que el primer render ya muestre el gráfico
  // completo en vez de un salto vacío→lleno que se veía como barras
  // superpuestas por la animación de entrada de G2.
  const effectiveSelectedFuel =
    selectedFuel && fuelTypes[selectedFuel] ? selectedFuel : Object.keys(fuelTypes)[0];

  useEffect(() => {
    const keys = Object.keys(fuelTypes);
    if (keys.length === 0) return;
    if (!selectedFuel || !fuelTypes[selectedFuel]) {
      onSelectedFuelChange(keys[0]);
    }
  }, [fuelTypes, selectedFuel, onSelectedFuelChange]);

  const { intensityData, averageData } = useMemo(
    () => buildFuelsIntensityData(resultsByYear, t, effectiveSelectedFuel),
    [resultsByYear, t, effectiveSelectedFuel]
  );

  // Renderizador de la leyenda estilo "Pills"
const CustomLegend = () => (
  <div style={{
    display: "flex",
    justifyContent: "flex-start", // 👈 Alinea a la izquierda
    gap: "10px",
    marginBottom: "20px",
    paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
    flexWrap: "wrap",
  }}>
    {years.map((year) => {
      const isActive = activeYears.includes(year);
      return (
        <div
          key={year}
          onClick={() => toggleYear(year)}
          style={{
            backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
            color: isActive ? "white" : "#9ca3af",
            padding: "4px 20px", // Un poco más compacto
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
            boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth:"100px"
          }}
        >
          {year}
        </div>
      );
    })}
  </div>
);

// Dentro de tu componente FuelsComparisonChart
const [activeYears, setActiveYears] = useState(years); 

// Función para alternar años
const toggleYear = (year) => {
  if (activeYears.includes(year)) {
    // Si solo queda uno, podrías elegir no dejar que se desactive el último
    setActiveYears(activeYears.filter(y => y !== year));
  } else {
    setActiveYears([...activeYears, year]);
  }
};

const filteredIntensityData = useMemo(() =>
  intensityData.filter(d => activeYears.includes(String(d.year))),
[intensityData, activeYears]);

const filteredAverageData = useMemo(() =>
  averageData.filter(d => activeYears.includes(String(d.year))),
[averageData, activeYears]);

  // 🔹 Leer colores desde CSS
const getCssVar = (name) =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();

const chartColors = {
  oldest: getCssVar("--chart-year-oldest"),
  prev: getCssVar("--chart-year-prev"),
  last: getCssVar("--chart-year-last"),
  avg: getCssVar("--chart-avg-line"),
};
;

// 1. Mapear años → colores fijos (Asegúrate de que esto esté actualizado)
const yearColorMap = useMemo(() => {
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: chartColors.oldest || "#cbd5e1", // Fallback si el CSS no carga
    [sorted[1]]: chartColors.prev || "#94a3b8",
    [sorted[2]]: chartColors.last || "#1e293b",
  };
}, [years, chartColors]);


  // 🧩 Escala Y explícita y compartida entre las dos series (Consumo y Promedio):
  // en vez de confiar en "sync" (que no alineaba correctamente), se calcula el
  // mismo domainMax numérico y se lo pasa a AMBOS children.
  const combinedMax = Math.max(
    0,
    ...filteredIntensityData.map((d) => d.value || 0),
    ...filteredAverageData.map((d) => d.value || 0),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  const sharedYScale = { domainMin: 0, domainMax: yDomainMax, nice: true };

  const config = {
    xField: "month",
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    axis: {
      y: { title: "kWhe / vehículo", labelFormatter: (v) => formatWhole(v) },
    },
    tooltip: {
      items: [
        (item) => ({
        name: item.type,
        value: `${formatWhole(item.value)} kWhe / vehículo`,
        color: yearColorMap[item.year],
      }),
      ],
    },
    legend: false,
    children: [
      {
        data: filteredIntensityData,
        type: "line",
        yField: "value",
        colorField: "year",
        style: { lineWidth: 4 },
        scale: { y: sharedYScale },
        connectNulls: false,
      },
      {
        data: filteredAverageData,
        type: "line",
        yField: "value",
        colorField: "year",
        axis: { y: false },
        scale: { y: sharedYScale },
        style: {
          lineWidth: 2,
          lineDash: [4, 8],
        },
        connectNulls: false,
      },
    ],
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      <div style={{
        display: "flex",
        justifyContent: "center",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}>
        {Object.entries(fuelTypes).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => onSelectedFuelChange(key)}
            style={{
              backgroundColor: effectiveSelectedFuel === key ? "#648EBC" : "#f3f4f6",
              color: effectiveSelectedFuel === key ? "white" : "#374151",
              border: "none",
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {cfg.label}
          </button>
        ))}
      </div>
      {/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
    {intensityData.length > 0 && <CustomLegend />}

      {intensityData.length ? (
        <DualAxes {...config} style={{ height: 400 }} />
      ) : (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 400,
          color: "#999",
        }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}
